import en from '@/locale/en'
import fr from '@/locale/fr'

/**
 * Get localized label for a company code
 * @param code The code to localize
 * @param codeType The type of code (legal_form, ape_naf, employee_count)
 * @param locale The current locale ('en' or 'fr')
 * @returns The localized label or the original code if not found
 */
export function getCompanyCodeLabel(
  code: string | undefined, 
  codeType: 'legal_form' | 'ape_naf' | 'employee_count',
  locale = 'fr'
): string {
  if (!code) return ''
  
  const localeData = locale === 'en' ? en : fr
  
  const codeMappings = {
    legal_form: localeData.legal_form_codes,
    ape_naf: localeData.ape_naf_codes,
    employee_count: localeData.employee_count_codes
  }
  
  const mapping = codeMappings[codeType]
  return mapping && (mapping as any)[code] ? (mapping as any)[code] : code
}

/**
 * Get localized label for legal form code
 * @param code The legal form code
 * @param locale The current locale
 * @returns The localized legal form label
 */
export function getLegalFormLabel(code: string | undefined, locale = 'fr'): string {
  return getCompanyCodeLabel(code, 'legal_form', locale)
}

/**
 * Get localized label for APE/NAF activity code
 * @param code The APE/NAF code
 * @param locale The current locale
 * @returns The localized activity label
 */
export function getActivityLabel(code: string | undefined, locale = 'fr'): string {
  return getCompanyCodeLabel(code, 'ape_naf', locale)
}

/**
 * Get localized label for employee count code
 * @param code The employee count code
 * @param locale The current locale
 * @returns The localized employee count label
 */
export function getEmployeeCountLabel(code: string | undefined, locale = 'fr'): string {
  return getCompanyCodeLabel(code, 'employee_count', locale)
}

/**
 * Get localized label for IDCC code
 * @param code The IDCC code
 * @param locale The current locale
 * @returns The localized IDCC label
 */
export function getIDCCLabel(code: string | undefined, locale = 'fr'): string {
  if (!code) return ''
  
  const localeData = locale === 'en' ? en : fr
  
  // IDCC codes are stored in legal_form_codes for now
  // You might want to create a separate IDCC mapping file
  return localeData.legal_form_codes && (localeData.legal_form_codes as any)[code] 
    ? (localeData.legal_form_codes as any)[code] 
    : code
} 